import { Trans, useTranslation } from '@blocs.i18n';
import Checkbox from '@minecraft.checkbox';
import React, { Dispatch, FC, SetStateAction } from 'react';
import { gqlAccount } from '@minecraft.graphql-types';
import { BasicContainer, BodyText, BodyTextSmall, FlexCol } from '@minecraft.atoms';
import { PlanType } from '../usePlans';
import { priceFormatter } from '../helpers/priceFormatter';

interface TrialPlanLegaleseProps {
  billingCode?: string;
  selectedPlan?: PlanType;
  countryCode?: gqlAccount['country']['code'];
}

interface AcceptTrialCheckboxProps {
  text?: string;
  checked?: boolean;
  onChange?: Dispatch<SetStateAction<boolean>>;
  isSticky?: boolean;
}

export const TrialTermLink: FC = ({ children }) => (
  <a href="https://corp.castingnetworks.com/terms-of-use" target="_blank" rel="noreferrer">
    {children}
  </a>
);

export const TrialPlanLegalese: FC<TrialPlanLegaleseProps> = ({ billingCode, selectedPlan, countryCode }) => {
  const { t } = useTranslation();
  const trialLengthDays = String(selectedPlan?.trialLengthDays ?? 30);
  const price = priceFormatter({
    price: Number(selectedPlan?.price || 0),
    currencyCode: selectedPlan?.currencyCode,
    t,
    countryCode,
  });

  return (
    <FlexCol gap="3" className="cn_w-full">
      <BodyText>{t('common:paywall.plan.trial.legal.cancelTime', { trialLengthDays })}</BodyText>
      <BodyText fontWeight="semibold">
        {t(`common:paywall.success.trial.${billingCode}.afterTrialCharge`, {
          trialLengthDays,
          price,
        })}
      </BodyText>
    </FlexCol>
  );
};

export const AcceptTrialCheckbox: FC<AcceptTrialCheckboxProps> = ({
  text = 'common:paywall.plan.trial.legal.trialTerms',
  checked = false,
  onChange = (_T: boolean) => null,
  isSticky = false,
}) => {
  return (
    <BasicContainer bgColor={isSticky ? 'selectionTag' : 'transparent'} padding={isSticky ? 3 : 0}>
      <Checkbox isChecked={checked} onChange={() => onChange(!checked)} testId="trial-terms-checkbox">
        <BodyText>
          <Trans i18nKey={text} components={{ a: <TrialTermLink /> }} />
        </BodyText>
      </Checkbox>
    </BasicContainer>
  );
};

export const FreePlanLegalText: FC = () => (
  <BasicContainer mx="8">
    <BodyTextSmall color="disabled" align="center" className="cn_atom_pb-1 cn_atom_pt-3">
      <Trans i18nKey="common:paywall.plan.registration.legal.terms" components={{ a: <TrialTermLink /> }} />
    </BodyTextSmall>
  </BasicContainer>
);
